import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { AuthService } from '../../core/auth.service';
import { I18nService } from '../../core/i18n.service';
import { TranslatePipe } from '../../core/translate.pipe';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  template: `
    <section class="login-page">
      <div class="login-shell">
        <div class="login-identity">
          <div class="login-emblem">
            <img class="login-logo" src="assets/ofs-logo.png" alt="Odisha Fire & Emergency Services logo">
          </div>
          <div class="login-identity-copy">
            <strong>{{ 'app.brand' | t }}</strong>
            <span>{{ 'app.name' | t }}</span>
          </div>
        </div>
        <form class="login-panel" [formGroup]="form" (ngSubmit)="submit()">
          <div class="login-form-heading">
            <span class="login-form-mark" aria-hidden="true"></span>
            <h1>{{ 'login.signIn' | t }}</h1>
          </div>
          <label class="login-field">
            {{ 'login.username' | t }}
            <input formControlName="username" autocomplete="username">
          </label>
          <div class="login-field">
            <label for="login-password">{{ 'login.password' | t }}</label>
            <div class="login-password-wrap">
              <input id="login-password" [type]="showPassword ? 'text' : 'password'" formControlName="password" autocomplete="current-password">
              <button class="login-password-toggle" type="button" (click)="showPassword = !showPassword"
                [attr.aria-label]="(showPassword ? 'login.hidePassword' : 'login.showPassword') | t"
                [attr.aria-pressed]="showPassword">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12Z"></path>
                  <circle cx="12" cy="12" r="2.5"></circle>
                  <path *ngIf="!showPassword" d="M4 20 20 4"></path>
                </svg>
              </button>
            </div>
          </div>
          <p class="error" *ngIf="error">{{ error }}</p>
          <button class="login-submit" type="submit" [disabled]="form.invalid || loading">{{ loading ? ('login.signingIn' | t) : ('login.signIn' | t) }}</button>
          <p class="login-help">{{ 'login.help' | t }}</p>
        </form>
      </div>
    </section>
  `
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  loading = false;
  error = '';
  showPassword = false;
  form = this.fb.nonNullable.group({
    username: ['', Validators.required],
    password: ['', Validators.required]
  });

  submit(): void {
    if (this.form.invalid) return;
    this.loading = true;
    this.error = '';
    this.auth.login(this.form.controls.username.value, this.form.controls.password.value).subscribe({
      next: () => this.router.navigateByUrl('/'),
      error: () => {
        this.error = this.i18n.t('login.invalid');
        this.loading = false;
      }
    });
  }
}
